import { useState, type FormEvent } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  CASE_TYPE_SUGGESTIONS,
  MATTER_STATUSES,
  matterCreateSchema,
  matterUpdateSchema,
  type ClientListRow,
  type MatterDetailRow,
} from "@themislex/shared";
import { ApiError, api } from "../../api/client";
import { useApi } from "../../api/hooks";
import {
  Button,
  Card,
  ErrorNote,
  Input,
  PageHeader,
  Select,
  Spinner,
  Textarea,
} from "../../components/ui";

const STATUS_LABELS: Record<string, string> = {
  intake: "Intake",
  active: "Active",
  on_hold: "On hold",
  closed: "Closed",
  archived: "Archived",
};

/** Wrapper: loads the matter (edit) and the client list (create). */
export default function MatterForm() {
  const { id } = useParams();
  const [params] = useSearchParams();
  const isEdit = Boolean(id);

  const existing = useApi<{ matter: MatterDetailRow }>(
    isEdit ? `/api/matters/${id}` : null,
  );
  const clients = useApi<{ clients: ClientListRow[] }>(isEdit ? null : "/api/clients");

  if (isEdit) {
    if (existing.loading) {
      return (
        <div className="flex justify-center py-16 text-muted">
          <Spinner className="h-6 w-6" />
        </div>
      );
    }
    if (existing.error || !existing.data) {
      return <ErrorNote>{existing.error ?? "Matter not found."}</ErrorNote>;
    }
    return (
      <MatterFormFields
        key={id}
        matter={existing.data.matter}
        clients={null}
        defaultClientId=""
      />
    );
  }

  return (
    <MatterFormFields
      key="new"
      matter={null}
      clients={clients}
      defaultClientId={params.get("clientId") ?? ""}
    />
  );
}

interface FormState {
  clientId: string;
  title: string;
  caseType: string;
  caseNumber: string;
  court: string;
  nextHearingDate: string;
  status: string;
  matterDescription: string;
}

function MatterFormFields({
  matter,
  clients,
  defaultClientId,
}: {
  matter: MatterDetailRow | null;
  clients: { loading: boolean; error?: string; data?: { clients: ClientListRow[] } } | null;
  defaultClientId: string;
}) {
  const navigate = useNavigate();
  const isEdit = Boolean(matter);

  const [form, setForm] = useState<FormState>(() => ({
    clientId: matter?.clientId ?? defaultClientId,
    title: matter?.title ?? "",
    caseType: matter?.caseType ?? "",
    caseNumber: matter?.caseNumber ?? "",
    court: matter?.court ?? "",
    nextHearingDate: matter?.nextHearingDate ?? "",
    status: matter?.status ?? "active",
    matterDescription: matter?.matterDescription ?? "",
  }));
  const [error, setError] = useState<string>();
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);

  const set = (key: keyof FormState, value: string) =>
    setForm((f) => ({ ...f, [key]: value }));

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setError(undefined);
    setFieldErrors({});

    const payload = {
      ...form,
      nextHearingDate: form.nextHearingDate || null,
    };
    const schema = isEdit ? matterUpdateSchema : matterCreateSchema;
    const parsed = schema.safeParse(payload);
    if (!parsed.success) {
      const errors: Record<string, string> = {};
      for (const issue of parsed.error.issues) {
        const key = String(issue.path[0] ?? "form");
        if (!errors[key]) errors[key] = issue.message;
      }
      setFieldErrors(errors);
      return;
    }

    setBusy(true);
    try {
      const path = isEdit ? `/api/matters/${matter?.id}` : "/api/matters";
      const body = await api<{ matter: MatterDetailRow }>(path, {
        method: isEdit ? "PUT" : "POST",
        json: parsed.data,
      });
      navigate(`/desk/matters/${body.matter.id}`);
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message);
        if (err.issues) {
          const errors: Record<string, string> = {};
          for (const issue of err.issues) {
            if (!errors[issue.path]) errors[issue.path] = issue.message;
          }
          setFieldErrors(errors);
        }
      } else {
        setError("Could not save the matter.");
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <PageHeader
        title={isEdit ? "Edit matter" : "New matter"}
        subtitle={
          isEdit
            ? "Changes are visible to the client immediately."
            : "Link the matter to a client and record its key details."
        }
        actions={
          isEdit ? (
            <Link to={`/desk/matters/${matter?.id}`} className="btn btn-secondary no-underline">
              Cancel
            </Link>
          ) : undefined
        }
      />

      <form onSubmit={submit} noValidate className="mx-auto max-w-3xl">
        <Card>
          {error && (
            <div className="mb-4">
              <ErrorNote>{error}</ErrorNote>
            </div>
          )}
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="sm:col-span-2">
              <label className="label" htmlFor="matter-client">
                Client
              </label>
              <select
                id="matter-client"
                className="input"
                required={!isEdit}
                disabled={isEdit}
                value={form.clientId}
                onChange={(e) => set("clientId", e.target.value)}
              >
                <option value="">
                  {clients?.loading ? "Loading clients…" : "Select a client…"}
                </option>
                {clients?.data?.clients.map((client) => (
                  <option key={client.id} value={client.id}>
                    {client.name} ({client.email})
                  </option>
                ))}
              </select>
              {fieldErrors.clientId && (
                <p className="mt-1 text-xs text-danger-fg" role="alert">
                  {fieldErrors.clientId}
                </p>
              )}
              {isEdit && (
                <p className="mt-1 text-xs text-muted">
                  Matters cannot be moved between clients.
                </p>
              )}
            </div>

            <div className="sm:col-span-2">
              <Input
                label="Matter title"
                required
                placeholder="e.g. Asha v. Metro Housing Society"
                value={form.title}
                onChange={(e) => set("title", e.target.value)}
                error={fieldErrors.title}
              />
            </div>

            <div>
              <Input
                label="Case type"
                required
                list="case-type-suggestions"
                placeholder="e.g. Property"
                value={form.caseType}
                onChange={(e) => set("caseType", e.target.value)}
                error={fieldErrors.caseType}
              />
              <datalist id="case-type-suggestions">
                {CASE_TYPE_SUGGESTIONS.map((type) => (
                  <option key={type} value={type} />
                ))}
              </datalist>
            </div>
            <Input
              label="Case number"
              placeholder="e.g. CIV-2026-0142"
              value={form.caseNumber}
              onChange={(e) => set("caseNumber", e.target.value)}
              error={fieldErrors.caseNumber}
            />
            <Input
              label="Court / forum"
              placeholder="e.g. Civil Judge (Senior Division), Agra"
              value={form.court}
              onChange={(e) => set("court", e.target.value)}
              error={fieldErrors.court}
            />
            <Input
              label="Next hearing date"
              type="date"
              value={form.nextHearingDate}
              onChange={(e) => set("nextHearingDate", e.target.value)}
              error={fieldErrors.nextHearingDate}
            />
            <Select
              label="Status"
              value={form.status}
              onChange={(e) => set("status", e.target.value)}
              error={fieldErrors.status}
            >
              {MATTER_STATUSES.map((s) => (
                <option key={s} value={s}>
                  {STATUS_LABELS[s]}
                </option>
              ))}
            </Select>
            <div className="sm:col-span-2">
              <Textarea
                label="Matter description"
                rows={5}
                placeholder="Summary of facts, arguments, strategy notes…"
                value={form.matterDescription}
                onChange={(e) => set("matterDescription", e.target.value)}
                error={fieldErrors.matterDescription}
              />
            </div>
          </div>

          <div className="mt-6 flex justify-end gap-2">
            <Link to="/desk/matters" className="btn btn-secondary no-underline">
              Cancel
            </Link>
            <Button type="submit" variant="primary" loading={busy}>
              {isEdit ? "Save changes" : "Create matter"}
            </Button>
          </div>
        </Card>
      </form>
    </>
  );
}
